import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { setSideBarColors } from "@/containers/Theme/ThemeHelper";
import channelClient from "@apis/channelClient";
import sitemapAPI from "@apis/sitemap";
import useAvailibleMenu from "./useAvailibleMenu";
import useAgentMenuItems, { AGENT_GROUP_STATE } from "./useAgentMenuItems";
import useDashboardMenuItems, { DASHBOARD_GROUP_STATE } from "./useDashboardMenuItems";
import { isHomeMenuReachable } from "./useNavigationModel";
import { MenuSourceConst, MenuTypeConst, type MenuType, type MenuRoute, type AvailibleMenuData } from "./menuTypes";
import type { RootState } from "@/types/RootState";
import type { LoginInfo } from "@/types/LoginInfo";
import type { Channel } from "@apis/channelClient/channelClient.dto";
import type { SiteMapItem } from "@/cleanup/pages/AllSiteMaps/types";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { canSeeHomeMaintenance } from "@/utils/maintenanceAccess";
import { canSeeAtOwnLevel } from "@/utils/partnerLevelFeature";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";

/**
 * Extended Channel with menu-specific fields
 * These fields may be returned by the API but aren't in the base DTO
 */
interface ExtendedChannel extends Channel {
  displayName?: string;
  showInHomeMenu?: boolean;
  homeMenuItemName?: string;
}

/**
 * Extended SiteMapItem with menu-specific fields
 * These fields may be returned by the API but aren't in the base DTO
 */
interface ExtendedSiteMapItem extends SiteMapItem {
  showInHomeMenu?: boolean;
  homeMenuItemName?: string;
}

/**
 * Extended menu route with additional category and type fields
 */
interface ExtendedMenuRoute extends MenuRoute {
  category?: string;
  type?: string;
  reportType?: string;
  channelId?: number;
  siteMapId?: number;
  excludeSubPages?: string[];
  title?: string;
}

/**
 * Return type of useHomeMenu hook
 */
interface HomeMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: ExtendedMenuRoute[];
}

/**
 * Hook for managing the home menu routes based on user permissions and features
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @param onMenuChange - Optional callback function triggered when menu is updated
 * @returns Menu data including routes, available paths, and loading state
 */
const useHomeMenu = (
  loginInfo: LoginInfo,
  onMenuChange: () => void = () => {}
): HomeMenuData => {
  const global = useSelector((state: RootState) => state.all);
  const { custumInfo, organisation } = loginInfo;

  const initialRoutes: ExtendedMenuRoute[] = [
    {
      view:
        hasFeature(FeatureKey.utilization_suez) ||
        (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD)),
      collapse: true,
      name: translate(LanguageKey.UserPermission_MENU_HOME_DASHBOARD_description_key),
      state: DASHBOARD_GROUP_STATE,
      icon: <span className="dashboard-menu-icon menu-icon"></span>,
      views: [],
    },
    {
      collapse: true,
      name: translate(LanguageKey.assets_key),
      state: "openAssets",
      icon: <span className="assets-menu-icon menu-icon"></span>,
      views: [
        {
          view: hasFeature(FeatureKey.loc_map) && hasPermission(UserPermission.MENU_HOME_ASSET_MAP),
          to: "/assets_location",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_ASSET_MAP_description_key),
        },
        {
          view: hasPermission(UserPermission.MENU_HOME_ASSET_LIST),
          to: "/tracked_assets",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_ASSET_LIST_description_key),
          subPages: ["/tracked_assets_update"],
        },
        {
          view:
            organisation?.organisationType !== "PARTNER" &&
            hasFeature(FeatureKey.asset_browser) &&
            hasFeature(FeatureKey.loc_map) &&
            hasPermission(UserPermission.MENU_HOME_ASSET_MAP),
          to: "/asset_browser",
          layout: "",
          name: translate(LanguageKey.sidebar_asset_browser_key),
        },
        {
          view:
            organisation?.organisationType !== "PARTNER" &&
            hasPermission(UserPermission.MENU_HOME_TAGS),
          to: "/asset_tags_manage_admin",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_TAGS_description_key),
        },
        {
          view:
            organisation?.organisationType !== "PARTNER" &&
            hasPermission(UserPermission.MENU_HOME_ASSET_TYPES),
          to: "/asset_types_overview",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_ASSET_TYPES_description_key),
        },
        {
          view: hasPermission(UserPermission.MANUFACTURING),
          to: "/manufacturing_info_view",
          layout: "",
          name: translate(LanguageKey.manufacturing_history_key),
        },
        {
          view:
            organisation?.organisationType !== "PARTNER" &&
            hasFeature(FeatureKey.location_history) &&
            hasPermission(UserPermission.MENU_HOME_LOCATION_HISTORY),
          to: "/location_history",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_LOCATION_HISTORY_description_key),
        },
      ],
    },
    {
      // R8: starts hidden — useAgentMenuItems turns it on only once the org has
      // at least one enrolled agent (on top of feature key + MENU_HOME_AGENT).
      view: false,
      collapse: true,
      name: translate(LanguageKey.agents_section_title_key),
      state: AGENT_GROUP_STATE,
      icon: <span className="agents-menu-icon menu-icon"></span>,
      views: [],
    },
    {
      view: organisation?.organisationType !== "PARTNER",
      collapse: true,
      name: translate(LanguageKey.site_maps_key),
      state: "openSiteMaps",
      icon: <span className="site-maps-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            hasFeature(FeatureKey.loc_site_map) && hasPermission(UserPermission.MENU_HOME_SITE_MAP),
          to: "/all_site_maps",
          layout: "",
          name: translate(LanguageKey.all_site_maps_key),
          subPages: ["/site_map_view"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.geozones_key),
      state: "openGeozone",
      icon: <span className="geozones-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            hasFeature(FeatureKey.geozone_db) && hasPermission(UserPermission.MENU_HOME_GEOZONE_OVERVIEW),
          to: "/geozones_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_GEOZONE_OVERVIEW_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.geozone_list) && hasPermission(UserPermission.MENU_HOME_GEOZONE_LIST),
          to: "/geozones_list",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_GEOZONE_LIST_description_key),
          subPages: ["/geozone_individual"],
        },
        {
          view:
            hasFeature(FeatureKey.geozone_taglist) &&
            hasPermission(UserPermission.MENU_HOME_GEOZONE_TAGS),
          to: "/geozones_tag_list",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_GEOZONE_TAGS_description_key),
        },
        {
          view:
            hasPermission(UserPermission.MENU_HOME_GEOZONE_VISIT_LIST) &&
            hasFeature(FeatureKey.geozone_visit),
          to: "/geozonevisit_list",
          layout: "",
          name: translate(LanguageKey.featurecategory_geozone_visits_name),
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.sidebar_assets_utilization_key),
      state: "openUtilization",
      icon: <span className="utilization-active-menu-icon menu-icon"></span>,
      views: [
        {
          // STIC-15987: a customer on utilization_dashboards; a partner only with the
          // legacy utilization_multi_org grant (its own-level KPIs); SYSTEM never.
          view:
            canSeeAtOwnLevel(FeatureKey.utilization_dashboards, FeatureKey.utilization_multi_org, loginInfo) &&
            hasPermission(UserPermission.MENU_HOME_DEVICE_UTILIZATION_KPI_LIST),
          to: "/utilization",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_DEVICE_UTILIZATION_KPI_LIST_description_key),
          subPages: ["/utilization_graph_and_list"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.custom_data_key),
      state: "openCustomData",
      icon: <span className="custom-data-menu-icon menu-icon"></span>,
      view: hasPermission(UserPermission.MENU_HOME_CUSTOM_DATA_LIST),
      views: [],
    },
    {
      collapse: true,
      title: translate(LanguageKey.manage_processes_key),
      name: translate(LanguageKey.manage_processes_key),
      state: "openProcesses",
      icon: <span className="flow-circle-menu-icon menu-icon"></span>,
      view: organisation?.organisationType !== "SYSTEM",
      views: [
        {
          view:
            hasFeature(FeatureKey.process_manager) &&
            hasPermission(UserPermission.MENU_HOME_PROCESS_LIST),
          to: "/processes",
          layout: "",
          name: translate(LanguageKey.process_list_key),
          subPages: ["/process_statistics"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.maintenance_key),
      state: "openMaintenances",
      icon: <span className="maintenance-menu-icon menu-icon"></span>,
      view: organisation?.organisationType !== "SYSTEM",
      views: [
        {
          view:
            canSeeHomeMaintenance(loginInfo) &&
            hasPermission(UserPermission.MENU_HOME_MAINTENANCE_OVERVIEW),
          to: "/maintenance_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_MAINTENANCE_OVERVIEW_description_key),
        },
        {
          view:
            canSeeHomeMaintenance(loginInfo) &&
            hasPermission(UserPermission.MENU_HOME_MAINTENANCE_PROGRAMS),
          to: "/maintenance_programs_stats",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_MAINTENANCE_PROGRAMS_description_key),
          subPages: [
            "/maintenance_programs_details",
            "/start_maintenance_program",
          ],
        },
        {
          view:
            canSeeHomeMaintenance(loginInfo) &&
            hasPermission(UserPermission.MENU_HOME_UPCOMING_MAINTENANCE),
          to: "/maintenance_upcoming",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_UPCOMING_MAINTENANCE_description_key),
        },
        {
          view:
            canSeeHomeMaintenance(loginInfo) &&
            hasPermission(UserPermission.MENU_HOME_DONE_MAINTENANCE),
          to: "/maintenance_done",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_DONE_MAINTENANCE_description_key),
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.alerts_key),
      state: "openAlerts",
      icon: <span className="alerts-menu-icon menu-icon"></span>,
      view: organisation?.organisationType !== "SYSTEM",
      views: [
        {
          view:
            hasFeature(FeatureKey.alert_activedb) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_ACTIVE_DASHBOARD),
          to: "/active_alerts_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_ACTIVE_DASHBOARD_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.alert_activelist) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_ACTIVE_LIST),
          to: "/active_alerts_list",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_ACTIVE_LIST_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.alert_histdb) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_HISTORICAL_DASHBOARD),
          to: "/historical_alerts_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_HISTORICAL_DASHBOARD_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.alert_histlist) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_HISTORICAL_LIST),
          to: "/historical_alerts_list",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_HISTORICAL_LIST_description_key),
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.reports_key),
      state: "openReports",
      icon: <span className="report-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            hasFeature(FeatureKey.report_generator) &&
            hasPermission(UserPermission.MENU_HOME_REPORT_LIST),
          to: "/reports",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_REPORT_LIST_description_key),
          subPages: ["/create_report_home"],
        },
        {
          view:
            hasFeature(FeatureKey.report_generator) &&
            hasPermission(UserPermission.MENU_HOME_REPORT_SCHEDULES),
          to: "/report_schedule",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_REPORT_SCHEDULES_description_key),
        },
      ],
    },
  ];
  const [routes, setRoutes] = useState<ExtendedMenuRoute[]>(initialRoutes);
  const [firstInit, setFirstInit] = useState(true);
  const [data, setData] = useState<HomeMenuData>({
    type: MenuTypeConst.MAIN,
    menuSource: MenuSourceConst.HOME,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/tracked_assets",
  });

  // Favourited dashboards + "All dashboards". Shared with the own-level menu,
  // which carries the same group for logins that have no Home menu (STIC-16186
  // R1).
  //
  // SidebarBody calls all three menu hooks unconditionally, so this one runs
  // for SYSTEM and for a partner without legacy_partner_home_menu too — logins
  // that can never select this menu (rule 4 and the Home button are both gated
  // on `homeMenuReachable`, and it is the own level that carries the group for
  // them). Skipping there is not just a saved request: SidebarBody blocks its
  // routing on `homeMenu.loading || ...`, so the fetch delayed the sidebar for
  // logins that had no use for the result.
  useDashboardMenuItems(setRoutes, {
    skip: !isHomeMenuReachable(loginInfo),
    tagWithDashboardCategory: true,
    onApplied: () => {
      setFirstInit(false);
      onMenuChange();
    },
  });

  // Favourited agents + "All agents", the same arrangement (STIC-16186 R2).
  useAgentMenuItems(setRoutes, {
    enabled: isHomeMenuReachable(loginInfo),
    orgId: organisation?.id,
    onApplied: () => {
      setFirstInit(false);
      onMenuChange();
    },
  });

  const availibleMenuData = useAvailibleMenu(routes, "/tracked_assets");

  useEffect(() => {
    if (firstInit) return;
    // Dashboard/agent/channel/site-map menu items load asynchronously and are
    // appended to `routes` after the first render. `availibleMenuData` is recomputed
    // one render later, so when `firstInit` flips it can still reflect the stale
    // pre-async routes (empty `availableMenuPaths` + a `/tracked_assets` `defaultPath`).
    // Applying that prematurely flips `loading` to false against an inconsistent menu,
    // which makes the post-login landing redirect bounce a dashboard-only user to
    // /no_access instead of their first available menu (STIC-15267). Only apply once
    // the computed menu reflects the current routes.
    if (availibleMenuData.routes !== routes) return;
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData, firstInit]);

  // The Dashboards and Agents groups are filled by the two hooks called above.
  useEffect(() => {
    setData({ ...data, loading: true });

    // Fetch channels for custom data menu
    if (hasPermission(UserPermission.MENU_HOME_CUSTOM_DATA_LIST)) {
      channelClient
        .findByOrg(organisation?.id || 0)
        .then((response) => {
          const channels = response.data as ExtendedChannel[];
          updateCustomDataViews(channels);
        })
        .catch(() => {
          updateCustomDataViews([]);
        });
    } else {
      updateCustomDataViews([]);
    }

    // Fetch site maps for home menu (hidden for partner accounts — skip the fetch)
    if (
      organisation?.organisationType !== "PARTNER" &&
      hasFeature(FeatureKey.loc_site_map) &&
      hasPermission(UserPermission.MENU_HOME_SITE_MAP)
    ) {
      sitemapAPI
        .getByOrgCheckVisibility({ orgId: organisation?.id })
        .then((response) => {
          const siteMaps = (response.data?.data || []) as ExtendedSiteMapItem[];
          updateSiteMapViews(siteMaps);
        })
        .catch(() => {
          updateSiteMapViews([]);
        });
    } else {
      updateSiteMapViews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global.reloadSidebar]);

  useEffect(() => {
    setSideBarColors(custumInfo);
  }, [custumInfo]);

  function updateCustomDataViews(channels: ExtendedChannel[]): void {
    const customDataViews: ExtendedMenuRoute[] = [];

    // Add a menu item for each channel that has showInHomeMenu enabled
    const visibleChannels = channels.filter(
      (channel) => channel.showInHomeMenu
    );

    for (let i = 0; i < visibleChannels.length; i++) {
      const channel = visibleChannels[i];
      customDataViews.push({
        view: hasPermission(UserPermission.MENU_HOME_CUSTOM_DATA_LIST),
        to: "/custom_data_channel/" + channel.id,
        layout: "",
        type: "channel",
        name: channel.homeMenuItemName || channel.displayName || channel.name,
        channelId: channel.id,
        category: "customData",
      });
    }

    setRoutes((oldRoutes) => {
      // Remove Custom Data section if no visible channels, otherwise update views
      if (visibleChannels.length === 0) {
        return oldRoutes.filter((x) => x.state !== "openCustomData");
      } else {
        const route = oldRoutes.find((x) => x.state === "openCustomData");
        if (route) {
          route.views = [];
          route.views.push(...customDataViews);
        }
        return [...oldRoutes];
      }
    });
    onMenuChange();
  }

  function updateSiteMapViews(siteMaps: ExtendedSiteMapItem[]): void {
    setRoutes((oldRoutes) => {
      // If no site maps exist, remove the entire Site maps section
      if (siteMaps.length === 0) {
        return oldRoutes.filter((x) => x.state !== "openSiteMaps");
      }

      const siteMapViews: ExtendedMenuRoute[] = [];

      // Add menu items for site maps with showInHomeMenu enabled (above "All site maps")
      const visibleSiteMaps = siteMaps.filter(
        (siteMap) => siteMap.showInHomeMenu
      );

      for (let i = 0; i < visibleSiteMaps.length; i++) {
        const siteMap = visibleSiteMaps[i];
        siteMapViews.push({
          view: hasPermission(UserPermission.MENU_HOME_SITE_MAP),
          to: "/site_map_view/" + siteMap.id,
          layout: "",
          type: "sitemap",
          name: siteMap.homeMenuItemName || siteMap.name,
          siteMapId: siteMap.id,
          category: "siteMap",
        });
      }

      // Add "All site maps" at the end
      // excludeSubPages prevents "All site maps" from being highlighted when viewing
      // a site map that has its own menu item in the home menu
      siteMapViews.push({
        view:
          hasFeature(FeatureKey.loc_site_map) && hasPermission(UserPermission.MENU_HOME_SITE_MAP),
        to: "/all_site_maps",
        layout: "",
        name: translate(LanguageKey.all_site_maps_key),
        subPages: ["/site_map_view"],
        excludeSubPages: visibleSiteMaps.map((sm) => "/site_map_view/" + sm.id),
      });

      const route = oldRoutes.find((x) => x.state === "openSiteMaps");
      if (route) {
        route.views = [];
        route.views.push(...siteMapViews);
      }
      return [...oldRoutes];
    });
    onMenuChange();
  }

  return data;
};

export default useHomeMenu;
